import React, {
  useEffect,
  useState,
  useMemo,
  forwardRef,
  useCallback,
} from "react";
import { Form } from "antd";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, parseSelectOptions } from "utils/formUtils";
import message from "components/CustomMessage";
import GenericMappingForm from "../GenericMappingForm";

const PolarisMappingForm = forwardRef(({ disableProjectFields = false, ...props }, ref) => {
  const [branchList, setBranchList] = useState({});
  const [projectList, setProjectList] = useState([]);
  const [project, setProject] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
        login_id: props.loginConfigId,
      });
    promise
      .then((response) => {
        const { projects } = response;
        setProjectList(projects);
      })
      .catch(() => {
        message.error("Problem fetching projects");
      });

    return cancel;
  }, [props.loginConfigId, props.toolName]);

  const fetchBranches = useCallback(
    (project) => {
      if (!projectList[project] && project) {
        const { promise, cancel } =
          ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
            login_id: props.loginConfigId,
            project,
          });

        promise
          .then((response) => {
            const { projects } = response;
            setBranchList((branchList) => ({
              ...branchList,
              [project]: projects,
            }));
          })
          .catch(() => {
            message.error("Problem fetching branches");
          });

        return cancel;
      }
    },
    [props.loginConfigId, props.toolName, projectList]
  );

  useEffect(() => {
    return fetchBranches(project);
  }, [project, fetchBranches]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { projectId, branchId } = config;
    setProject(projectId);
    return {
      projectId,
      branchId,
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id }) 
    };
  }, []);

  const onProjectChange = useCallback(
    (value, field) => {
      setProject(value);
      const { name } = field;
      const configurations = form.getFieldValue("configurations");
      configurations[name] = {
        ...configurations[name],
        branchId: undefined,
      };
      form.setFieldsValue({ configurations: configurations });
    },
    [form]
  );

  const getExtraFieldsData = useCallback(
    (config) => {
      const { projectId, branchId } = config;
      const projectName = projectList.find((project) => project.id === projectId)
      ?.name;
      const branchName = branchList[projectId].find(
        (branch) => branch.id === branchId
      )?.name; 
      return {
        projectId,
        projectName,
        branchId,
        branchName,
        projectAndBranchName: projectName + ":" + branchName,
        ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
      };
    },
    [projectList, branchList]
  );

  const extraFields = useMemo(
    () => [
      {
        name: "projectId",
        colSpan: "10",
        type: FieldTypes.SELECT,
        label: "Project",
        disabled: disableProjectFields,
        options: parseSelectOptions(projectList),
        required: true,
        props: {
          onChange: onProjectChange,
          autoFocus: !disableProjectFields
        },
      },
      {
        name: "branchId",
        colSpan: "13",
        type: FieldTypes.SELECT,
        label: "Branch Name",
        required: true,
        disabled: disableProjectFields,
        loading: project && !branchList[project],
        props: {
          getFormListOptions: (fieldKey) => {
            const configs = form.getFieldValue("configurations");
            const projectId = configs[fieldKey]?.projectId;
            return parseSelectOptions(branchList[projectId]);
          },
        },
      },
    ],
    [project, onProjectChange, projectList, branchList, form, disableProjectFields]
  );

  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      {...props}
    />
  );
});

export default PolarisMappingForm;
